// Global Instructions Rule Applied!

import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import type { RootState } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { exportJsonToExcel } from '../../../core/services/utilitiesService.ts'
import { buildReportingGridExcelPayload } from '../../../state_manager/utils/reportingGridExcelExport.ts'
import { fetchAllFundInvestmentsForExport } from '../../shared/table_grid_export/fetchAllFilteredForExport.ts'

import TableView from '../../../core/components/TableView/TableView.tsx'
import ViewToolbar from '../../../core/components/ViewToolbar/index.tsx'
import { ViewConfig } from '../../../core/components/ViewConfig/index.ts'
import type { ViewConfigColumn } from '../../../core/components/ViewConfig/types.ts'
import { createTableColumns } from '../../../ui_configs/table_grid_columns.ts'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { TABLE_ACCOUNTS } from '../../../state_manager/globals.ts'
import { recordHasTotalLabel, stripTotalTextFromCellNode } from '../../../core/utils/recordRowBold.ts'

import { useFundInvestmentSummary } from '../hooks/useFundInvestmentSummary.ts'
import { useFundInvestmentSummaryReportId } from '../../../state_manager/hooks/useFundInvestmentSummaryReportId.ts'
import { useFreezeLeadingColumns } from '../../../state_manager/hooks/useFreezeLeadingColumns.ts'
import { useReportingGridColumnsWithPreferences } from '../../../state_manager/hooks/useReportingGridColumnsWithPreferences.ts'
import { getReportingGridColumnsForReportId } from '../../../state_manager/utils/reportingGridColumns.ts'
import { getAddColumnsCatalogForReport } from '../../../state_manager/utils/reportingColumnCatalog.ts'

interface Props {
  fundId: string | null
  fundName?: string | null
}

export function FundInvestmentSummaryTable({ fundId, fundName }: Props): React.ReactElement | null {
  const { openFormInTab } = useTabManagerContext()

  const reportId = useFundInvestmentSummaryReportId()
  const freezeLeadingColumns = useFreezeLeadingColumns(reportId)
  const reportingConfigs = useSelector((s: RootState) => s.globalConfigs?.reporting_configs ?? [])
  const reportingColumnDefaults = useSelector((s: RootState) => s.globalConfigs?.reporting_column_defaults ?? [])
  const reportingColumns = useSelector((s: RootState) => s.globalConfigs?.reporting_columns ?? [])

  const {
    data,
    loading,
    filterOptions,
    filterOptionsLoading,
    activeFilters,
    activeSorting,
    hasMore,
    currentPage,
    preferenceRow,
    handleSetFilters,
    handleSetSorting,
    handleResetFilters,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault,
    refetch,
    defaultTableFilters,
    defaultTableSorting
  } = useFundInvestmentSummary(fundId)

  // Grid columns honor user column-order/visibility preferences when set.
  const gridColumns = useReportingGridColumnsWithPreferences(reportId, preferenceRow)
  const viewConfigColumns: ViewConfigColumn[] = gridColumns

  // Add-columns catalog for the Edit Columns drawer.
  const addColumnsCatalog = useMemo(
    () =>
      getAddColumnsCatalogForReport(
        reportId,
        {
          reporting_columns: reportingColumns,
          reporting_column_defaults: reportingColumnDefaults
        },
        { primaryModuleKey: 'accounts' }
      ),
    [reportId, reportingColumns, reportingColumnDefaults]
  )

  const catalogFieldNameOrder = useMemo(
    () => [...addColumnsCatalog.deal, ...addColumnsCatalog.related].map((c) => c.valueKey),
    [addColumnsCatalog]
  )

  const defaultTableColumnKeys = useMemo(() => {
    if (!reportId) return null
    const rows = getReportingGridColumnsForReportId(reportId, {
      reporting_configs: reportingConfigs,
      reporting_column_defaults: reportingColumnDefaults,
      reporting_columns: reportingColumns
    })
    return rows.map((r) => r.valueKey)
  }, [reportId, reportingConfigs, reportingColumnDefaults, reportingColumns])

  const columnByValueKey = useMemo(() => {
    const m = new Map<string, ViewConfigColumn>()
    for (const c of addColumnsCatalog.deal) m.set(c.valueKey, c)
    for (const c of addColumnsCatalog.related) m.set(c.valueKey, c)
    for (const c of viewConfigColumns) {
      if (!m.has(c.valueKey)) m.set(c.valueKey, c)
    }
    return m
  }, [addColumnsCatalog, viewConfigColumns])

  const resolveViewConfigColumn = useCallback(
    (valueKey: string) => columnByValueKey.get(valueKey),
    [columnByValueKey]
  )

  const columnLayout = useMemo(
    () => ({
      onPersistFieldOrder: persistColumnFieldOrder,
      onResetToDefaults: resetColumnLayoutToDefault,
      resolveColumn: resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    }),
    [
      persistColumnFieldOrder,
      resetColumnLayoutToDefault,
      resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    ]
  )

  // Build table columns + name-cell click handler.
  const { columns: baseColumns, frozenColumns } = useMemo(
    () => createTableColumns({ columns: gridColumns, freezeLeadingColumns, fallbackRecordModuleKey: 'companies' }),
    [gridColumns, freezeLeadingColumns]
  )

  const openRecordFullColumns = useMemo(
    () => new Set(gridColumns.filter((c) => c.fieldKey === 'open-record-full').map((c) => c.valueKey)),
    [gridColumns]
  )

  const firstColumnValueKey = gridColumns[0]?.valueKey

  const columns = useMemo(() => {
    return baseColumns.map((col) => {
      const prevRender = col.render
      if (!prevRender) return col
      const isFirstColumn = firstColumnValueKey != null && col.key === firstColumnValueKey
      const opensRecordFull = openRecordFullColumns.has(String(col.key))
      return {
        ...col,
        render: (value: unknown, record: Record<string, unknown>, index: number, editingContext: unknown) => {
          const node = prevRender!(value, record, index, editingContext)
          const isTotalRow = recordHasTotalLabel(record)
          const renderedNode = isTotalRow && !isFirstColumn ? stripTotalTextFromCellNode(node) : node

          if (!opensRecordFull || isTotalRow) return renderedNode

          const accountId = typeof record.accounts__id === 'string' ? record.accounts__id : ''
          if (!accountId) return renderedNode

          return (
            <button
              type='button'
              className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
              onClick={() => openFormInTab(TABLE_ACCOUNTS, accountId, String(record.accounts__name ?? ''))}
            >
              {renderedNode}
            </button>
          )
        }
      }
    })
  }, [baseColumns, firstColumnValueKey, openRecordFullColumns, openFormInTab])

  const dataSource = useMemo(
    () =>
      data.map((row, i) => {
        const id =
          typeof row.accounts__id === 'string' && row.accounts__id ? row.accounts__id : `total-${i}`
        return { ...row, id, key: id }
      }),
    [data]
  )

  // Convert activeFilters → currentFilters object for TableView header dropdowns.
  const currentFilters = useMemo(() => {
    if (!Array.isArray(activeFilters)) return activeFilters ?? {}
    return activeFilters.reduce((acc: Record<string, any>, f) => {
      acc[f.key] = f.value
      return acc
    }, {})
  }, [activeFilters])

  const currentSortConfig = useMemo(() => {
    if (!Array.isArray(activeSorting) || activeSorting.length === 0) return null
    const first = activeSorting[0]
    return { column: first.key, direction: first.direction }
  }, [activeSorting])

  const [exportLoading, setExportLoading] = useState(false)
  const handleExport = useCallback(async () => {
    if (!fundId) return
    setExportLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const { data: rows, error } = await fetchAllFundInvestmentsForExport(fundId, activeSorting, activeFilters, token)
      if (error) {
        showErrorToast(error)
        return
      }
      const records = (rows.filter((r: any) => r && r.accounts__id) as Record<string, unknown>[])
      if (records.length === 0) {
        toast.info('No rows to export')
        return
      }
      const payload = buildReportingGridExcelPayload(gridColumns, records)
      const namePart = fundName ? `${fundName} - ` : ''
      const filename = `${namePart}Investment Summary - ${dayjs().format('YYYY-MM-DD')}.xlsx`
      const result = await exportJsonToExcel({ ...payload, filename }, token)
      if (result.success) {
        toast.success(`${filename} exported successfully`)
      } else {
        showErrorToast(result.error || 'Export failed')
      }
    } finally {
      setExportLoading(false)
    }
  }, [fundId, fundName, activeSorting, activeFilters, gridColumns])

  if (!fundId) return null

  return (
    <div className='flex-1 min-h-0 h-full flex flex-col gap-2'>
      <ViewToolbar
        moduleKey={TABLE_ACCOUNTS}
        recordTitleField='accounts__name'
        searchPlaceholder='Search Investments...'
        searchService='fund-investment-summary'
        searchExtraContext={{ fundId }}
        searchClickOpensFullForm
        addButtonEnabled={false}
        variant='secondary'
        leftSlot={
          <div className='flex items-center gap-2 text-white'>
            <span className='w-2 h-2 rounded-full bg-indigo-300' />
            <span className='font-semibold text-sm'>Investment Summary</span>
          </div>
        }
        activeFilters={activeFilters}
        onRefreshData={() => refetch()}
        onFilterChange={(filters) => handleSetFilters(Array.isArray(filters) ? filters : [])}
        defaultFilters={defaultTableFilters}
        onResetFilters={handleResetFilters}
        exportEnabled
        onExport={handleExport}
        exportLoading={exportLoading}
        beforeAddSlot={
          <ViewConfig
            moduleTitle='Fund Investment Summary'
            columns={viewConfigColumns}
            filterOptions={filterOptions}
            filterOptionsLoading={filterOptionsLoading}
            addColumnsCatalog={addColumnsCatalog}
            addColumnsTabLabels={{ primary: 'Company', related: 'Related' }}
            columnLayout={columnLayout}
            filteringConfig={{
              currentFilters: activeFilters,
              defaultFilters: defaultTableFilters,
              onApplyFilters: async (filters) => {
                handleSetFilters(filters)
              },
              onResetToDefaults: async () => {
                handleSetFilters(defaultTableFilters)
              }
            }}
            sortingConfig={{
              currentSorting: activeSorting,
              defaultSorting: defaultTableSorting,
              onApplySorting: async (sorting) => {
                handleSetSorting(sorting)
              },
              onResetToDefaults: async () => {
                handleSetSorting(defaultTableSorting)
              }
            }}
          />
        }
      />

      <div className='flex-1 min-h-0 h-full' style={{ minHeight: 320 }}>
        <TableView
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          hasMore={hasMore}
          currentPage={currentPage}
          currentFilters={currentFilters}
          onFiltersChange={(filters) => {
            const filterArray = Object.entries(filters).map(([key, value]) => ({ key, value }))
            handleSetFilters(filterArray)
          }}
          onSortChange={(sortConfig: any) => {
            if (sortConfig) {
              handleSetSorting([{ key: sortConfig.column, direction: sortConfig.direction }])
            } else {
              handleSetSorting([])
            }
          }}
          filterOptions={filterOptions}
          filterOptionsLoading={filterOptionsLoading}
          currentSortConfig={currentSortConfig}
          frozenColumns={frozenColumns}
          totalsMode='sticky-bottom'
          rowClassName={(record) => (recordHasTotalLabel(record) ? 'font-bold' : undefined)}
          headerStyle={{ padding: '5px', minHeight: 36, backgroundColor: '#f5f5f5' }}
        />
      </div>
    </div>
  )
}

export default FundInvestmentSummaryTable
